import { Textarea } from '@headlessui/react';
import { Trash2Icon } from 'lucide-react';
import { useCallback, useId, useLayoutEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

function MultilineEditable(props: {
	placeholder: string;
	emptyValue: string;
	value: string | null;
	onChange: (value: string | null) => void;

	className?: string;
	textAreaClassName?: string;
	valueButtonClassName?: string;
}) {
	const {
		value,
		textAreaClassName,
		onChange,
		placeholder,
		emptyValue,
		className,
		valueButtonClassName,
	} = props;
	const [isEditing, setIsEditing] = useState(false);
	const originalValueRef = useRef(value);
	const textAreaRef = useRef<HTMLTextAreaElement>(null);
	const instructionsId = useId();

	// Auto-resize functionality
	const adjustTextareaHeight = useCallback(() => {
		if (textAreaRef.current) {
			// Reset height to 'auto' to let the textarea shrink to its natural content height
			textAreaRef.current.style.height = 'auto';
			// Set height to the exact content height (scrollHeight) to prevent scrollbars
			textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`;
		}
	}, []);

	useLayoutEffect(() => {
		if (isEditing && textAreaRef.current) {
			textAreaRef.current.focus();
			textAreaRef.current.select();
			adjustTextareaHeight();

			// Scroll into view for mobile keyboards
			// Use setTimeout to ensure the textarea is rendered and focused first
			setTimeout(() => {
				if (textAreaRef.current) {
					textAreaRef.current.scrollIntoView({
						behavior: 'smooth',
						block: 'center',
						inline: 'nearest',
					});
				}
			}, 100);
		}
	}, [isEditing, adjustTextareaHeight]);

	// Handle keyboard appearance on mobile
	useLayoutEffect(() => {
		if (!isEditing) {
			return;
		}

		const handleResize = () => {
			// Small delay to allow keyboard animation to complete
			setTimeout(() => {
				if (textAreaRef.current) {
					textAreaRef.current.scrollIntoView({
						behavior: 'smooth',
						block: 'center',
						inline: 'nearest',
					});
				}
			}, 300);
		};

		// Listen for viewport changes (keyboard appearing/disappearing)
		window.addEventListener('resize', handleResize);

		// Also listen for visual viewport changes (more accurate for mobile keyboards)
		if (window.visualViewport) {
			window.visualViewport.addEventListener('resize', handleResize);
		}

		return () => {
			window.removeEventListener('resize', handleResize);
			if (window.visualViewport) {
				window.visualViewport.removeEventListener('resize', handleResize);
			}
		};
	}, [isEditing]);

	const handleSave = useCallback(() => {
		originalValueRef.current = value;
		onChange(value);
		setIsEditing(false);
	}, [value, onChange]);

	const handleCancel = useCallback(() => {
		onChange(originalValueRef.current);
		setIsEditing(false);
	}, [onChange]);

	const handleStartEditing = useCallback(() => {
		setIsEditing(true);
	}, []);

	const handleDelete = useCallback(() => {
		onChange('');
		originalValueRef.current = '';
		setIsEditing(false);
	}, [onChange]);

	const handleTextareaChange = useCallback(
		(e: React.ChangeEvent<HTMLTextAreaElement>) => {
			onChange(e.target.value);
			adjustTextareaHeight();
		},
		[onChange, adjustTextareaHeight],
	);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
			if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
				e.preventDefault();
				handleSave();
			} else if (e.key === 'Escape') {
				e.preventDefault();
				handleCancel();
			}
		},
		[handleSave, handleCancel],
	);

	const handleBlur = useCallback(
		(_e: React.FocusEvent<HTMLTextAreaElement>) => {
			handleSave();
		},
		[handleSave],
	);

	if (isEditing) {
		return (
			<fieldset
				className={cn('space-y-1.5 sm:space-y-2', className)}
				aria-label="Edit text content"
			>
				<Textarea
					ref={textAreaRef}
					value={value ?? ''}
					onChange={handleTextareaChange}
					onKeyDown={handleKeyDown}
					onBlur={handleBlur}
					className={cn(
						'w-full resize-none overflow-hidden rounded-lg border border-gray-300 bg-white px-3 py-2 font-medium text-gray-700 text-sm leading-relaxed focus:border-gray-400 focus:outline-none sm:py-2.5',
						textAreaClassName,
					)}
					placeholder={placeholder}
					aria-label="Edit text content"
					aria-describedby={instructionsId}
				/>
				<div id={instructionsId} className="sr-only">
					Press Ctrl+Enter (or Cmd+Enter on Mac) to save, or Escape to cancel
				</div>
				<div className="flex gap-1.5 sm:gap-2">
					<button
						type="button"
						onClick={handleSave}
						className="rounded-md bg-gray-900 px-3 py-1.5 font-medium text-white text-xs hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
						aria-label="Save changes"
						title="Save changes (Ctrl+Enter)"
					>
						Save
					</button>
					<button
						type="button"
						onClick={handleCancel}
						className="rounded-md border border-gray-300 px-3 py-1.5 font-medium text-gray-600 text-xs hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
						aria-label="Cancel editing"
						title="Cancel editing (Escape)"
					>
						Cancel
					</button>
				</div>
			</fieldset>
		);
	}

	return (
		<div className={cn('', className)}>
			{value && value !== '' ? (
				<div className="flex gap-1.5 sm:gap-2">
					<button
						type="button"
						onClick={handleStartEditing}
						className={cn(
							'flex-1 rounded-lg bg-gray-50 px-3 py-2 text-left font-medium text-gray-700 text-sm leading-relaxed transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 sm:py-2.5',
							valueButtonClassName,
						)}
						aria-label={`Edit text content: ${value}`}
						title="Click to edit text content"
					>
						{value}
					</button>
					<button
						type="button"
						onClick={handleDelete}
						className="flex size-9 shrink-0 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
						aria-label="Delete text content"
						title="Delete text content"
					>
						<Trash2Icon className="size-4" aria-hidden="true" />
					</button>
				</div>
			) : (
				<button
					type="button"
					onClick={handleStartEditing}
					className="w-full rounded-lg border border-gray-300 border-dashed px-3 py-2 text-left font-medium text-gray-400 text-xs hover:border-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
					aria-label={`Add text content: ${emptyValue}`}
					title="Click to add text content"
				>
					{emptyValue}
				</button>
			)}
		</div>
	);
}

export { MultilineEditable };
